import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const TextFieldWrapper = styled.div`
  position: relative;
  width: 200px;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  
  .MuiOutlinedInput-root {
    border-radius: 20px;
  }

  input {
    &::placeholder {
      opacity: 0;
    }
  }
`;

const PlaceholderOverlay = styled.div`
  position: absolute;
  left: 40px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  z-index: 1;
`;

const ShortcutKey = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 0px;
  opacity: 0.7;
  margin: 0 1px;
  margin-top: -2px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: rgba(0, 0, 0, 0.4);
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1);
  vertical-align: middle;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const ShortcutKeyLong = styled(ShortcutKey)`
  min-width: 28px;
  padding: 0 2px;
`;

const SearchTextField = ({ inputRef, placeholder = 'Search', value, disableKeyboardShortcut = false, ...props }) => {
  const isMac = useMemo(() => {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }, []);

  const showPlaceholder = !value || value === '';

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (disableKeyboardShortcut) {
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        if (inputRef?.current) {
          inputRef.current.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [disableKeyboardShortcut, inputRef]);

  return (
    <TextFieldWrapper>
      <StyledTextField
        {...props}
        inputRef={inputRef}
        value={value}
        placeholder=" " // Space to reserve space for overlay
        InputProps={{
          ...props.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {showPlaceholder && (
        <PlaceholderOverlay>
          <span>{placeholder}</span>
          {isMac ? (
            <>
              <ShortcutKey>⌘</ShortcutKey>
              <ShortcutKey>F</ShortcutKey>
            </>
          ) : (
            <>
              <ShortcutKeyLong>Ctrl</ShortcutKeyLong>
              <ShortcutKey>F</ShortcutKey>
            </>
          )}
        </PlaceholderOverlay>
      )}
    </TextFieldWrapper>
  );
};

export { SearchTextField };
